import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import { AddSitemapEntry } from "../api"

export default function EditDialog({
  isOpen,
  onClose,
  initialData = null,
  onSave,
  parentRefreshKey,
}) {
  const [formData, setFormData] = useState({
    url: "",
    frequency: "daily",
    priority: 0.8,
    origin: "manual",
    type: "SitemapEntry", // This is crucial for CMS type
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Frequency options
  const frequencyOptions = [
    { value: "always", name: "Always" },
    { value: "hourly", name: "Hourly" },
    { value: "daily", name: "Daily" },
    { value: "weekly", name: "Weekly" },
    { value: "monthly", name: "Monthly" },
    { value: "yearly", name: "Yearly" },
    { value: "never", name: "Never" },
  ]

  // Priority options (0.0 to 1.0 in 0.1 increments)
  const priorityOptions = Array.from({ length: 11 }, (_, i) => ({
    value: i / 10,
    name: (i / 10).toFixed(1),
  }))

  // Initialize form data when the dialog opens with initial data
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          url: initialData.url || "",
          frequency: initialData.frequency || "daily",
          priority:
            initialData.priority !== undefined ? initialData.priority : 0.8,
          origin: initialData.origin || "manual",
          _id: initialData._id,
          type: "SitemapEntry",
        })
      } else {
        // Reset form for new entry
        setFormData({
          url: "",
          frequency: "daily",
          priority: 0.8,
          origin: "manual",
          type: "SitemapEntry",
        })
      }
      setErrors({})
    }
  }, [initialData, isOpen, parentRefreshKey])

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }))
    }
  }

  // Validate the form
  const validateForm = () => {
    const newErrors = {}

    // Validate URL
    if (!formData.url) {
      newErrors.url = "URL is required"
    } else if (!/^\/[a-zA-Z0-9\-_\/]*$/.test(formData.url)) {
      newErrors.url = "URL must start with / and contain only valid characters"
    }

    // Set the errors and return if form is valid
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)

    try {
      const response = await AddSitemapEntry({
        ...formData,
      })

      if (response.error) {
        showNotification({
          message: response.message || "Failed to save sitemap entry",
          className: "bg-admin_error text-white",
        })
      } else {
        showNotification({
          message: "Sitemap entry saved successfully",
          className: "bg-admin_primary text-admin_text_inv",
        })

        // Notify parent component to refresh its data
        if (onSave) {
          onSave(response)
        }

        onClose()
      }
    } catch (error) {
      showNotification({
        message: "An error occurred while saving",
        className: "bg-admin_error text-white",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin"
      className="w-full md:w-2/3 lg:w-1/2 md:absolute md:top-[65px] h-full md:h-auto z-200"
      closeOnEscape
    >
      <div className="p-6">
        <Typography variant="admin_h2" className="text-admin_text mb-6">
          {initialData ? "Edit Sitemap Entry" : "Add Sitemap Entry"}
        </Typography>

        <div className="space-y-4">
          {/* URL Field */}
          <div className="flex flex-col gap-2">
            <Typography variant="admin_txt" className="text-admin_text">
              URL
            </Typography>
            <Input
              variant="admin"
              value={formData.url}
              autoFocus={isOpen}
              onChange={(e) => handleInputChange("url", e.target.value)}
              placeholder="/page-path"
              className={`w-full ${errors.url ? "border-admin_error" : ""}`}
            />
            <div className="h-5">
              {" "}
              {/* Fixed height for error message to prevent layout shift */}
              {errors.url && (
                <Typography variant="admin_txt" className="text-admin_error">
                  {errors.url}
                </Typography>
              )}
            </div>
            <Typography
              variant="admin_txt"
              className="text-admin_text text-opacity-70"
            >
              Must start with / (e.g., /about, /blog/post-1)
            </Typography>
          </div>

          {/* Frequency Field */}
          <div className="flex flex-col gap-2">
            <Typography variant="admin_txt" className="text-admin_text">
              Frequency
            </Typography>
            <DropDownSelect
              variant="admin"
              options={frequencyOptions}
              value={{ value: formData.frequency, name: formData.frequency }}
              onChange={(selected) =>
                handleInputChange("frequency", selected[0]?.value)
              }
              valueField="value"
              displayField="name"
              placeholder="Select frequency"
              searchEnabled
            />
          </div>

          {/* Priority Field */}
          <div className="flex flex-col gap-2">
            <Typography variant="admin_txt" className="text-admin_text">
              Priority
            </Typography>
            <DropDownSelect
              variant="admin"
              options={priorityOptions}
              value={{
                value: formData.priority,
                name: formData.priority.toFixed(1),
              }}
              onChange={(selected) =>
                handleInputChange("priority", selected[0]?.value)
              }
              valueField="value"
              displayField="name"
              placeholder="Select priority"
              searchEnabled
            />
          </div>

          {/* Origin Field (read-only for existing entries) */}
          {initialData &&
            initialData.origin &&
            initialData.origin !== "manual" && (
              <div className="flex flex-col gap-2">
                <Typography variant="admin_txt" className="text-admin_text">
                  Origin
                </Typography>
                <Input
                  variant="admin"
                  value={formData.origin}
                  disabled
                  className="w-full bg-admin_neutral_1"
                />
                <Typography
                  variant="admin_txt"
                  className="text-admin_text text-opacity-70"
                >
                  Source of this sitemap entry (read-only)
                </Typography>
              </div>
            )}
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <Button
            variant="admin_secondary"
            className="text-admin_text"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="admin_primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
